// Catálogo de marcas (fabricante/marca del producto). Inventario → Catálogo → Marcas.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { CLASIF_MARCA, Campo, Marca, Modal, Switch, fieldCls, inv } from './shared';

const vacio = { codigo: '', nombre: '', descripcion: '', clasificacion: 'terceros' as Marca['clasificacion'], es_activo: true };
const clasifLabel = (c: Marca['clasificacion']) => CLASIF_MARCA.find((x) => x.value === c)?.label ?? c;

export function Marcas() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: marcas = [], isFetching } = useQuery<Marca[]>({
    queryKey: ['inv-marcas'], queryFn: () => inv.list<Marca>('marca', '?select=*&order=nombre'),
  });

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);

  const abrir = (m?: Marca) => {
    if (m) { setEditId(m.id); setForm({ codigo: m.codigo ?? '', nombre: m.nombre, descripcion: m.descripcion ?? '', clasificacion: m.clasificacion ?? 'terceros', es_activo: m.es_activo }); }
    else { setEditId(null); setForm(vacio); }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: async () => {
      const body = { codigo: form.codigo || null, nombre: form.nombre, descripcion: form.descripcion || null, clasificacion: form.clasificacion, es_activo: form.es_activo };
      return editId ? inv.update('marca', editId, body) : inv.create('marca', body);
    },
    onSuccess: () => { toast.success('Marca guardada'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['inv-marcas'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => inv.remove('marca', id),
    onSuccess: () => { toast.success('Marca eliminada'); void qc.invalidateQueries({ queryKey: ['inv-marcas'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Marcas</h1>
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nueva marca</Button>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Catálogo ({marcas.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Clasificación</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {marcas.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Sin marcas</td></tr>}
              {marcas.map((m) => (
                <tr key={m.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3">{m.nombre}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{m.codigo || '—'}</td>
                  <td className="py-2 pr-3">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-xs ${
                      m.clasificacion === 'propia' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                      : m.clasificacion === 'rebate' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                      : 'bg-muted text-muted-foreground'}`}>{clasifLabel(m.clasificacion ?? 'terceros')}</span>
                  </td>
                  <td className="py-2 pr-3">{m.es_activo ? 'Activa' : 'Inactiva'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(m)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar"
                      onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${m.nombre}"?`, { peligro: true })) eliminar.mutate(m.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar marca' : 'Nueva marca'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Código"><input className={fieldCls} value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} /></Campo>
            <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></Campo>
            <Campo label="Clasificación">
              <select className={fieldCls} value={form.clasificacion} onChange={(e) => setForm({ ...form, clasificacion: e.target.value as Marca['clasificacion'] })}>
                {CLASIF_MARCA.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </Campo>
            <Campo label="Descripción"><textarea className={`${fieldCls} h-20`} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} /></Campo>
            <Switch checked={form.es_activo} onChange={(v) => setForm({ ...form, es_activo: v })} label="Activa" />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
